import { Component, computed, effect, inject, input, resource } from "@angular/core";
import { VIRTUAL_SCROLL_STRATEGY } from "@angular/cdk/scrolling";
import { merge } from "rxjs";

import { TaxprepT1Card, type IColumnData, type ColumnFilterSelection } from "@app/views-interfaces";
import { IMergedStatusObjectModel } from "@app/views-interfaces/status-object-model.interface";
import { IiFirmUserRolesWithUsersMap } from "@app/interfaces/ifirm-roles.interfaces";
import { CardDisplayService } from "@shared/card-display";
import { DashboardsService, FiltersService, IFirmRolesService, UnassignedUserId, UsersService } from "@shared/index";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AssigneesColumnScrollStrategy, VIRTUAL_SCROLL_THRESHOLD } from "./assignees-column-scroll-strategy";
import { ColumnStateService } from "./column-state.service";
import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";
import { compareString } from "@shared/utils";

interface IColumnsResourceParams {
    status: IMergedStatusObjectModel;
    rolesWithUsers: IiFirmUserRolesWithUsersMap;
}

@Component({
    selector: "assignees-columns-wrapper",
    templateUrl: "./assignees-columns-wrapper.component.html",
    styleUrl: "./assignees-columns-wrapper.component.scss",
    standalone: false,
    host: { class: "wk-ac-wrapper fancy-scrollbar-newUI", id: "virtual-scroller-wrapper" },
    providers: [ColumnStateService, AssigneesColumnScrollStrategy, { provide: VIRTUAL_SCROLL_STRATEGY, useExisting: AssigneesColumnScrollStrategy }],
})
export class AssigneesColumnsWrapperComponent {
    private readonly filtersService = inject(FiltersService);
    private readonly usersService = inject(UsersService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly strategy = inject(AssigneesColumnScrollStrategy);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly dragAndDropService = inject(DragAndDropService);

    readonly isInitialized = input<boolean>(true);
    readonly selectedStatus = input.required<IMergedStatusObjectModel>();
    readonly selectedStepId = input.required<string>();
    readonly columnFilters = input<ColumnFilterSelection>();

    private readonly columnsResource = resource<IColumnData<TaxprepT1Card>[], IColumnsResourceParams>({
        defaultValue: [],
        params: () => ({
            status: this.selectedStatus(),
            rolesWithUsers: this.iFirmRolesService.rolesWithUsers(),
        }),
        loader: ({ params }) => this.loadColumns(params),
    });

    protected readonly columns = computed<IColumnData<TaxprepT1Card>[]>(() => {
        const rawColumns = this.columnsResource.value() ?? [];
        return this.applyColumnLevelFilters(rawColumns);
    });
    protected readonly noCards = computed<boolean>(() => this.columns().reduce((acc, column) => acc + column.cards.length, 0) === 0);
    readonly displayedUserColumnsCount = computed<number>(() => (this.noCards() ? 0 : Math.max(this.columns().length - 1, 0)));
    protected readonly dragDisabled = computed<boolean>(() => this.displayedUserColumnsCount() > VIRTUAL_SCROLL_THRESHOLD);

    constructor() {
        // TODO refine this, we should just update cards with filter, not re-generate them
        merge(this.filtersService.filtersChangedEvent$, this.cardDisplayService.cardChangedEvent$)
            .pipe(this.dragAndDropService.deferWhileDragging(), takeUntilDestroyed())
            .subscribe(() => {
                this.columnsResource.reload();
            });

        effect(() => {
            const ids = this.columns().map((col) => col.id);
            this.strategy.setColumnIds(ids);
        });
    }

    private async loadColumns(params: IColumnsResourceParams): Promise<IColumnData<TaxprepT1Card>[]> {
        const { status, rolesWithUsers } = params;
        const statusId = status.TaxReturnStatus?.Id;
        if (!statusId) return [];

        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        const useiFirmUserId = this.dashboardsService.useiFirmUserId(currentDashboard);
        const cardModels = await this.cardDisplayService.getModel();
        const applicableModel = cardModels?.find((model) => model.statusId === statusId);
        if (!applicableModel) return [];

        return this.buildColumns(applicableModel.cards as TaxprepT1Card[], status, rolesWithUsers, useiFirmUserId);
    }

    private buildColumns(
        cards: TaxprepT1Card[],
        status: IMergedStatusObjectModel,
        rolesWithUsers: IiFirmUserRolesWithUsersMap,
        useiFirmUserId: boolean,
    ): IColumnData<TaxprepT1Card>[] {
        const roleId = status.Role?.Id;
        const statusId = status.TaxReturnStatus.Id;

        // Card-level filtering and sorting must happen here (not in ColumnComponent) so that
        // column ordering reflects the number of cards actually shown.
        const filteredCards = this.filtersService.filterCards(cards, statusId ? [statusId] : undefined) as TaxprepT1Card[];
        this.filtersService.sortCards(filteredCards);

        const cardsByUserId = new Map<string, TaxprepT1Card[]>();
        const unassignedCards: TaxprepT1Card[] = [];
        for (const card of filteredCards) {
            const mapping = card.roleUserMapping.find((m) => m.roleId == roleId);
            const userId = mapping?.userId;
            if (!userId || userId === "" || userId === UnassignedUserId) {
                unassignedCards.push(card);
                continue;
            }
            const bucket = cardsByUserId.get(userId);
            if (bucket) {
                bucket.push(card);
            } else {
                cardsByUserId.set(userId, [card]);
            }
        }

        const unassignedColumn: IColumnData<TaxprepT1Card> = {
            id: UnassignedUserId,
            columnName: "General.UnassignedFem",
            statuses: [status],
            state: "",
            isCustomStatus: false,
            fromCache: false,
            cards: unassignedCards,
            active: true,
            assignedUserId: UnassignedUserId,
        };

        if (!roleId) return [unassignedColumn];

        // TODO This should really take all users that have a role, and all users that are assigned to cards.
        const userColumns = Array.from(rolesWithUsers.values())
            .filter((usersByRoles) => usersByRoles.Id === roleId)
            .flatMap((usersByRoles) => Array.from(usersByRoles.Users.values()))
            .map((user): IColumnData<TaxprepT1Card> => {
                const userCards = cardsByUserId.get(user.Id) ?? cardsByUserId.get(user.IFirmUserId) ?? [];
                return {
                    id: useiFirmUserId && user.IFirmUserId ? user.IFirmUserId : user.Id,
                    columnName: user.FullName ?? "",
                    statuses: [status],
                    state: "",
                    isCustomStatus: false,
                    fromCache: false,
                    cards: userCards,
                    active: user.Active,
                    assignedUserId: useiFirmUserId && user.IFirmUserId ? user.IFirmUserId : user.Id,
                };
            })
            .filter((column) => column.active || column.cards.length > 0)
            .sort((a, b) => {
                if (a.active !== b.active) return a.active ? 1 : -1;
                if (b.cards.length !== a.cards.length) return b.cards.length - a.cards.length;
                if (a.columnName !== b.columnName) return compareString(a.columnName, b.columnName);
                return 0;
            });

        return [unassignedColumn, ...userColumns];
    }

    protected trackByColumnId(_index: number, column: IColumnData<TaxprepT1Card>): string {
        return column.id;
    }

    private applyColumnLevelFilters(columns: IColumnData<TaxprepT1Card>[]): IColumnData<TaxprepT1Card>[] {
        const filters = this.columnFilters();
        if (!filters) return columns;

        const hasUsersFilter = filters.users.length > 0;
        const hasGroup1Filter = filters.userGroup1.length > 0;
        const hasGroup2Filter = filters.userGroup2.length > 0;

        if (!hasUsersFilter && !hasGroup1Filter && !hasGroup2Filter) {
            return columns;
        }

        const selectedUserIds = hasUsersFilter ? new Set(filters.users.map((u) => u.value as string)) : null;
        const selectedGroup1Values = hasGroup1Filter ? new Set(filters.userGroup1.map((g) => g.value as string)) : null;
        const selectedGroup2Values = hasGroup2Filter ? new Set(filters.userGroup2.map((g) => g.value as string)) : null;
        const usersById = this.usersService.usersById();

        return columns.filter((column) => {
            if (column.id === UnassignedUserId) return true;

            const assignedUserId = column.assignedUserId;
            if (!assignedUserId) return true;

            const userData = usersById?.fromId?.get(assignedUserId) ?? usersById?.fromUserId?.get(assignedUserId);

            if (selectedUserIds) {
                const matchesUser =
                    selectedUserIds.has(assignedUserId) ||
                    (userData?.IFirmUserId && selectedUserIds.has(userData.IFirmUserId)) ||
                    (userData?.Id && selectedUserIds.has(userData.Id));
                if (!matchesUser) return false;
            }

            if (selectedGroup1Values && userData) {
                const userGroup1 = userData.GroupFilteringId1?.toString() ?? "-1";
                if (!selectedGroup1Values.has(userGroup1)) return false;
            }

            if (selectedGroup2Values && userData) {
                const userGroup2 = userData.GroupFilteringId2?.toString() ?? "-1";
                if (!selectedGroup2Values.has(userGroup2)) return false;
            }

            return true;
        });
    }
}
